import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios';
import {
  getClassQuiz,
  getLiveGame,
  getReplayQuizzes,
  startAttempt,
  submitAnswer
} from '../api';
import {
  IAnswerCheck,
  IAttemptResult,
  IClassQuiz,
  ILiveGame,
  IReplayQuiz,
  IReplayQuizzesPage
} from '../types';

export type tCurrentStatus = 'idle' | 'loading' | 'loaded' | 'failed';

/** One answer of the run on screen: on its way to the server, not arrived, or checked. */
export type tAnswerCheck =
  | { choiceIndex: number; status: 'checking' | 'failed' }
  | ({ choiceIndex: number; status: 'checked' } & Omit<IAnswerCheck, 'attempt'>);

interface IClassQuizState {
  liveGame: ILiveGame | null;
  liveGameRequestId: string | null;
  requestedQuizId: string | null;
  currentRequestId: string | null;
  current: IClassQuiz | null;
  currentStatus: tCurrentStatus;
  /** The self-study run on screen. */
  attemptId: string | null;
  attemptStarted: boolean;
  /** The server did not start the run: a live game of the quiz is open. */
  liveBlocked: boolean;
  /** By the question's index in the quiz. */
  checks: Record<number, tAnswerCheck>;
  /** The scored run, with the last answer's check. */
  result: IAttemptResult | null;
  replayQuizzes: IReplayQuiz[];
  replayNextPage: number | null;
  replayStatus: tCurrentStatus;
}

const initialState: IClassQuizState = {
  liveGame: null,
  liveGameRequestId: null,
  requestedQuizId: null,
  currentRequestId: null,
  current: null,
  currentStatus: 'idle',
  attemptId: null,
  attemptStarted: false,
  liveBlocked: false,
  checks: {},
  result: null,
  replayQuizzes: [],
  replayNextPage: null,
  replayStatus: 'idle'
};

export const fetchClassQuiz = createAsyncThunk('classQuiz/fetchOne', (id: string) =>
  getClassQuiz(id)
);

export const fetchLiveGame = createAsyncThunk('classQuiz/fetchLiveGame', () =>
  getLiveGame()
);

export const fetchReplayQuizzes = createAsyncThunk<
  IReplayQuizzesPage,
  number | undefined,
  { state: { classQuiz: IClassQuizState } }
>('classQuiz/fetchReplayQuizzes', (page) => getReplayQuizzes(page), {
  condition: (_page, { getState }) => getState().classQuiz.replayStatus !== 'loading'
});

/** A game that ends between two pages moves a quiz, so a later page can repeat one: show it once. */
const withoutRepeats = (shown: IReplayQuiz[], next: IReplayQuiz[]) => {
  const shownIds = new Set(shown.map((quiz) => quiz.id));
  return [...shown, ...next.filter((quiz) => !shownIds.has(quiz.id))];
};

interface IRunArg {
  quizId: string;
  attemptId: string;
}

interface IAnswerArg extends IRunArg {
  questionIndex: number;
  choiceIndex: number;
}

const LIVE_GAME = 'live_game';

type tThunkApi = { state: { classQuiz: IClassQuizState }; rejectValue: typeof LIVE_GAME };

/** True when the server did not start the run because the quiz is played live now (409). */
const startRefusedForLiveGame = async ({
  quizId,
  attemptId
}: IRunArg): Promise<boolean> => {
  try {
    await startAttempt(quizId, attemptId);
    return false;
  } catch (error) {
    const response = (error as AxiosError<{ error?: string }>)?.response;

    if (response?.status === 409 && response.data?.error === LIVE_GAME) return true;
    throw error;
  }
};

export const startClassQuizAttempt = createAsyncThunk<void, IRunArg, tThunkApi>(
  'classQuiz/startAttempt',
  async (run, { rejectWithValue }) => {
    if (await startRefusedForLiveGame(run)) return rejectWithValue(LIVE_GAME);

    return undefined;
  }
);

/**
 * One answer per question: a second tap while it is checked sends nothing. Only an answer that
 * did not reach the server is sent again, with the same pick. A run whose start did not reach
 * the server is started first; the same id starts it once.
 */
export const answerClassQuizQuestion = createAsyncThunk<
  IAnswerCheck,
  IAnswerArg,
  tThunkApi
>(
  'classQuiz/answer',
  async (
    { quizId, attemptId, questionIndex, choiceIndex },
    { getState, rejectWithValue }
  ) => {
    if (
      !getState().classQuiz.attemptStarted &&
      (await startRefusedForLiveGame({ quizId, attemptId }))
    )
      return rejectWithValue(LIVE_GAME);

    return submitAnswer(quizId, attemptId, { questionIndex, choiceIndex });
  },
  {
    condition: ({ attemptId, questionIndex, choiceIndex }, { getState }) => {
      const { classQuiz } = getState();
      const check = classQuiz.checks[questionIndex];

      return (
        classQuiz.attemptId === attemptId &&
        (!check || (check.status === 'failed' && check.choiceIndex === choiceIndex))
      );
    }
  }
);

const classQuizSlice = createSlice({
  name: 'classQuiz',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchClassQuiz.pending, (state, { meta }) => {
        state.requestedQuizId = meta.arg;
        state.currentRequestId = meta.requestId;
        state.current = null;
        state.currentStatus = 'loading';
        state.liveBlocked = false;
      })
      .addCase(fetchClassQuiz.fulfilled, (state, { payload, meta }) => {
        if (meta.requestId !== state.currentRequestId) return;
        state.current = payload;
        state.currentStatus = 'loaded';
      })
      .addCase(fetchClassQuiz.rejected, (state, { meta }) => {
        if (meta.requestId !== state.currentRequestId) return;
        state.currentStatus = 'failed';
      })
      .addCase(fetchLiveGame.pending, (state, { meta }) => {
        state.liveGameRequestId = meta.requestId;
      })
      .addCase(fetchLiveGame.fulfilled, (state, { payload, meta }) => {
        if (meta.requestId !== state.liveGameRequestId) return;
        state.liveGame = payload;
      })
      .addCase(fetchLiveGame.rejected, (state, { meta }) => {
        if (meta.requestId === state.liveGameRequestId) state.liveGame = null;
      })
      .addCase(fetchReplayQuizzes.pending, (state) => {
        state.replayStatus = 'loading';
      })
      .addCase(fetchReplayQuizzes.fulfilled, (state, { payload, meta }) => {
        state.replayStatus = 'loaded';
        state.replayQuizzes = meta.arg
          ? withoutRepeats(state.replayQuizzes, payload.quizzes)
          : payload.quizzes;
        state.replayNextPage = payload.nextPage;
      })
      .addCase(fetchReplayQuizzes.rejected, (state) => {
        state.replayStatus = 'failed';
      })
      .addCase(startClassQuizAttempt.pending, (state, { meta }) => {
        state.attemptId = meta.arg.attemptId;
        state.attemptStarted = false;
        state.liveBlocked = false;
        state.checks = {};
        state.result = null;
      })
      .addCase(startClassQuizAttempt.fulfilled, (state, { meta }) => {
        if (meta.arg.attemptId === state.attemptId) state.attemptStarted = true;
      })
      .addCase(startClassQuizAttempt.rejected, (state, { payload, meta }) => {
        if (payload === LIVE_GAME && meta.arg.attemptId === state.attemptId)
          state.liveBlocked = true;
      })
      .addCase(answerClassQuizQuestion.pending, (state, { meta }) => {
        const { questionIndex, choiceIndex } = meta.arg;

        state.checks[questionIndex] = { choiceIndex, status: 'checking' };
      })
      .addCase(answerClassQuizQuestion.fulfilled, (state, { payload, meta }) => {
        const { attemptId, questionIndex, choiceIndex } = meta.arg;
        const { attempt, ...check } = payload;

        if (attemptId !== state.attemptId) return;
        state.attemptStarted = true;
        state.checks[questionIndex] = { choiceIndex, status: 'checked', ...check };
        if (attempt) state.result = attempt;
      })
      .addCase(answerClassQuizQuestion.rejected, (state, { payload, meta }) => {
        const { attemptId, questionIndex, choiceIndex } = meta.arg;

        if (attemptId !== state.attemptId) return;
        state.checks[questionIndex] = { choiceIndex, status: 'failed' };
        if (payload === LIVE_GAME) state.liveBlocked = true;
      });
  }
});

export default classQuizSlice.reducer;
